import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from '../../lib/cn';
import {
  formatDateValue,
  formatMonthYear,
  getVisibleMonthCells,
  isAfterDate,
  isBetweenRange,
  isSameDate,
  monthLabels,
  weekdayLabels,
} from './DateRangePicker.helpers';
import { CalendarView, type DateRangeDraft } from './DateRangePicker.types';

interface DateRangeCalendarProps {
  draftRange: DateRangeDraft | undefined;
  maxDate?: Date | undefined;
  onDateSelect: (date: Date) => void;
  onViewChange: (view: CalendarView) => void;
  onVisibleMonthChange: (date: Date) => void;
  view: CalendarView;
  visibleMonth: Date;
}

export function DateRangeCalendar({
  draftRange,
  maxDate,
  onDateSelect,
  onViewChange,
  onVisibleMonthChange,
  view,
  visibleMonth,
}: DateRangeCalendarProps) {
  const year = visibleMonth.getFullYear();
  const month = visibleMonth.getMonth();
  const yearGroupStart = Math.floor(year / 12) * 12;

  function changeMonth(delta: number) {
    onVisibleMonthChange(new Date(year, month + delta, 1));
  }

  function changeYear(delta: number) {
    onVisibleMonthChange(new Date(year + delta, month, 1));
  }

  function changeDecade(delta: number) {
    onVisibleMonthChange(new Date(year + delta * 12, month, 1));
  }

  return (
    <div className="bg-popover w-[280px] p-3">
      <div className="mb-3 flex items-center justify-between">
        <IconButton
          label={previousButtonLabel(view)}
          onClick={() => {
            if (view === CalendarView.Days) changeMonth(-1);
            else if (view === CalendarView.Months) changeYear(-1);
            else changeDecade(-1);
          }}
        >
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
        </IconButton>
        <button
          type="button"
          className="hover:bg-accent cursor-pointer rounded-md px-3 py-1 text-sm font-semibold"
          onClick={() => {
            onViewChange(view === CalendarView.Days ? CalendarView.Months : CalendarView.Years);
          }}
        >
          {calendarTitle({ view, visibleMonth, year, yearGroupStart })}
        </button>
        <IconButton
          label={nextButtonLabel(view)}
          onClick={() => {
            if (view === CalendarView.Days) changeMonth(1);
            else if (view === CalendarView.Months) changeYear(1);
            else changeDecade(1);
          }}
        >
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
        </IconButton>
      </div>
      {view === CalendarView.Days ? (
        <DaysView
          draftRange={draftRange}
          maxDate={maxDate}
          onDateSelect={onDateSelect}
          visibleMonth={visibleMonth}
        />
      ) : null}
      {view === CalendarView.Months ? (
        <MonthsView
          selectedMonth={month}
          onMonthSelect={(nextMonth) => {
            onVisibleMonthChange(new Date(year, nextMonth, 1));
            onViewChange(CalendarView.Days);
          }}
        />
      ) : null}
      {view === CalendarView.Years ? (
        <YearsView
          selectedYear={year}
          yearGroupStart={yearGroupStart}
          onYearSelect={(nextYear) => {
            onVisibleMonthChange(new Date(nextYear, month, 1));
            onViewChange(CalendarView.Months);
          }}
        />
      ) : null}
    </div>
  );
}

function DaysView({
  draftRange,
  maxDate,
  onDateSelect,
  visibleMonth,
}: Pick<DateRangeCalendarProps, 'draftRange' | 'maxDate' | 'onDateSelect' | 'visibleMonth'>) {
  const days = getVisibleMonthCells(visibleMonth);

  return (
    <div>
      <div className="text-muted-foreground mx-auto grid w-fit grid-cols-7 gap-0.5 pb-2 text-center text-xs">
        {weekdayLabels.map((day) => (
          <span key={day} className="flex h-6 w-8 items-center justify-center">
            {day}
          </span>
        ))}
      </div>
      <div className="mx-auto grid w-fit grid-cols-7 gap-0.5">
        {days.map((date, index) => {
          if (!date) {
            return <span key={`blank-${String(index)}`} aria-hidden="true" />;
          }

          const disabled = maxDate ? isAfterDate(date, maxDate) : false;

          return (
            <button
              key={formatDateValue(date)}
              type="button"
              disabled={disabled}
              role="gridcell"
              aria-label={`Select ${formatDateValue(date)}`}
              aria-selected={isSameDate(date, draftRange?.from) || isSameDate(date, draftRange?.to)}
              className={dateButtonClass(date, draftRange, disabled)}
              onClick={() => {
                onDateSelect(date);
              }}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function MonthsView({
  onMonthSelect,
  selectedMonth,
}: {
  onMonthSelect: (month: number) => void;
  selectedMonth: number;
}) {
  return (
    <div className="grid grid-cols-3 gap-2 pt-2">
      {monthLabels.map((month, index) => (
        <button
          key={month}
          type="button"
          className={selectorButtonClass(selectedMonth === index)}
          onClick={() => {
            onMonthSelect(index);
          }}
        >
          {month}
        </button>
      ))}
    </div>
  );
}

function YearsView({
  onYearSelect,
  selectedYear,
  yearGroupStart,
}: {
  onYearSelect: (year: number) => void;
  selectedYear: number;
  yearGroupStart: number;
}) {
  return (
    <div className="grid grid-cols-3 gap-2 pt-2">
      {Array.from({ length: 12 }, (_, index) => yearGroupStart + index).map((year) => (
        <button
          key={year}
          type="button"
          className={selectorButtonClass(selectedYear === year)}
          onClick={() => {
            onYearSelect(year);
          }}
        >
          {year}
        </button>
      ))}
    </div>
  );
}

function IconButton({
  children,
  label,
  onClick,
}: {
  children: ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      className="text-muted-foreground hover:bg-accent hover:text-foreground flex h-8 w-8 cursor-pointer items-center justify-center rounded-md transition-colors"
      onClick={onClick}
    >
      {children}
    </button>
  );
}

function calendarTitle({
  view,
  visibleMonth,
  year,
  yearGroupStart,
}: {
  view: CalendarView;
  visibleMonth: Date;
  year: number;
  yearGroupStart: number;
}) {
  if (view === CalendarView.Days) return formatMonthYear(visibleMonth);
  if (view === CalendarView.Months) return String(year);
  return `${String(yearGroupStart)} - ${String(yearGroupStart + 11)}`;
}

function dateButtonClass(date: Date, range: DateRangeDraft | undefined, disabled: boolean) {
  const isStart = isSameDate(date, range?.from);
  const isEnd = isSameDate(date, range?.to);
  const isMiddle = isBetweenRange(date, range);

  return cn(
    'border-border/70 text-foreground hover:bg-accent hover:text-accent-foreground mx-auto flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border text-sm transition-colors',
    isMiddle && 'bg-accent/80 text-accent-foreground',
    (isStart || isEnd) && 'bg-primary text-primary-foreground hover:bg-primary border-primary',
    disabled &&
      'text-muted-foreground/50 pointer-events-none cursor-not-allowed opacity-50 hover:bg-transparent hover:text-muted-foreground/50',
  );
}

function nextButtonLabel(view: CalendarView) {
  if (view === CalendarView.Days) return 'Next month';
  if (view === CalendarView.Months) return 'Next year';
  return 'Next years';
}

function previousButtonLabel(view: CalendarView) {
  if (view === CalendarView.Days) return 'Previous month';
  if (view === CalendarView.Months) return 'Previous year';
  return 'Previous years';
}

function selectorButtonClass(selected: boolean) {
  return cn(
    'hover:bg-accent cursor-pointer rounded-md px-3 py-3 text-sm transition-colors',
    selected && 'bg-primary text-primary-foreground hover:bg-primary',
  );
}
